import { Divider } from "react-native-paper";
import {
    Form,
    Loading,
    InputControl,
    Spacer,
    TextInput,
    Alert,
    Button,
    useThemedStyle,
    StyleFunction,
    Headline,
} from "@venuepos/react-common";
import { GQTssStateEnum, GQFiskalyTss } from "graphql-sdk";
import { useTranslation } from "locales";
import React, { View } from "react-native";
import { NIL } from "uuid";
import { FiskalySettingsInput } from "lib";

export function FiskalySettingsForm({
    form,
    tssData,
    onRegisterTss,
    onTssStateAdvance,
    onChangePin,
}: {
    form: Form<FiskalySettingsInput>;
    tssData: GQFiskalyTss;
    onRegisterTss: () => Promise<void>;
    onTssStateAdvance: (nextState: GQTssStateEnum) => Promise<void>;
    onChangePin: () => Promise<void>;
}) {
    const [t] = useTranslation();
    const styles = useThemedStyle(styleFunc);

    const [{ values, errors }, { setValue }] = form;

    if (!values) {
        return <Loading />;
    }

    const hasOrganization =
        values && values.organizationId && values.organizationId !== NIL;
    const hasTss = hasOrganization && values.tssId && values.tssId !== NIL;

    return (
        <>
            <InputControl
                error={errors.organizationId}
                description={t(
                    "backoffice.integration.fiskaly.organization_id_description",
                    "This id used in the communication with Fiskaly and is set automatically when data for the Organization is filled in and stored on the Organization tab."
                )}
            >
                <TextInput
                    label={t(
                        "backoffice.integration.fiskaly.organization_id",
                        "Organization id"
                    )}
                    value={values.organizationId}
                    onChangeText={text => setValue("organizationId", text)}
                    disabled={true}
                />
            </InputControl>

            {!hasOrganization ? (
                <Alert type="warning">
                    {t(
                        "backoffice.integration.fiskaly.organization_not_registered",
                        "To be able to use the Fiskaly integration, an organization needs to be registered. Check the Organization tab."
                    )}
                </Alert>
            ) : null}
            <Spacer />
            <Divider />
            <Spacer />

            <InputControl
                error={errors.tssId}
                description={t(
                    "backoffice.integration.fiskaly.tss_id_description",
                    "TSS is a 'black box', that stores and signs data on transactions."
                )}
            >
                <TextInput
                    label={t("backoffice.integration.fiskaly.tss_id", "TSS id")}
                    value={values.tssId}
                    onChangeText={text => setValue("tssId", text)}
                    disabled={true}
                />
            </InputControl>
            <View style={styles.gap}>
                {!hasOrganization && !hasTss ? (
                    <Alert type="warning">
                        {t(
                            "backoffice.integration.fiskaly.no_organization_found_for_tss",
                            "An organization is needed to register a TSS. Please fix this first."
                        )}
                    </Alert>
                ) : hasOrganization && !hasTss ? (
                    <>
                        <Alert type="warning">
                            {t(
                                "backoffice.integration.fiskaly.no_tss_found",
                                "No TSS is registered yet. The Fiskaly integration cannot be used yet."
                            )}
                        </Alert>
                        <Spacer />
                        <Button
                            onPress={async () => {
                                await onRegisterTss();
                            }}
                            size="small"
                        >
                            {t(
                                "backoffice.integration.fiskaly.register_tss",
                                "Register TSS w/ Fiskaly"
                            )}
                        </Button>
                    </>
                ) : null}
                {tssData ? (
                    <>
                        <Headline size="h5" style={styles.smallHeader}>
                            {t(
                                "backoffice.integration.fiskaly.tss_lifecycle_header",
                                "TSS life cycle"
                            )}
                        </Headline>
                        <View style={styles.buttonHolder}>
                            <Button
                                disabled={!hasOrganization || hasTss}
                                onPress={async () => {
                                    await onRegisterTss();
                                }}
                                variant="outline"
                                size="tiny"
                                type="warning"
                                style={styles.smallButton}
                            >
                                {t(
                                    "backoffice.integration.fiskaly.tss_lifecycle_create",
                                    "Create"
                                )}
                            </Button>
                            <Button
                                disabled={
                                    tssData.state !== GQTssStateEnum.Created
                                }
                                onPress={async () => {
                                    await onTssStateAdvance(
                                        GQTssStateEnum.Uninitialized
                                    );
                                }}
                                variant="outline"
                                size="tiny"
                                style={styles.smallButton}
                            >
                                {t(
                                    "backoffice.integration.fiskaly.tss_lifecycle_prepare",
                                    "Prepare"
                                )}
                            </Button>
                            <Button
                                disabled={
                                    tssData.state !==
                                    GQTssStateEnum.Uninitialized
                                }
                                onPress={async () => {
                                    await onTssStateAdvance(
                                        GQTssStateEnum.Initialized
                                    );
                                }}
                                variant="outline"
                                size="tiny"
                                style={styles.smallButton}
                            >
                                {t(
                                    "backoffice.integration.fiskaly.tss_lifecycle_activate",
                                    "Activate"
                                )}
                            </Button>
                            <Button
                                disabled={
                                    tssData.state !== GQTssStateEnum.Initialized
                                }
                                onPress={async () => {
                                    await onTssStateAdvance(
                                        GQTssStateEnum.Disabled
                                    );
                                }}
                                variant="outline"
                                size="tiny"
                                type="warning"
                                style={styles.smallButton}
                            >
                                {t(
                                    "backoffice.integration.fiskaly.tss_lifecycle_disable",
                                    "Disable"
                                )}
                            </Button>
                            <Button
                                disabled={
                                    ![
                                        GQTssStateEnum.Disabled,
                                        GQTssStateEnum.Defective,
                                        GQTssStateEnum.Deleted,
                                        GQTssStateEnum.Evicted,
                                    ].includes(tssData.state)
                                }
                                onPress={async () => {
                                    await onRegisterTss();
                                }}
                                variant="outline"
                                size="tiny"
                                type="warning"
                                style={styles.smallButton}
                            >
                                {t(
                                    "backoffice.integration.fiskaly.tss_lifecycle_replace",
                                    "Replace"
                                )}
                            </Button>
                        </View>

                        {tssData.state === GQTssStateEnum.Initialized ? (
                            <>
                                <Headline size="h5" style={styles.smallHeader}>
                                    {t(
                                        "backoffice.integration.fiskaly.tss_lifecycle_actions",
                                        "TSS Actions"
                                    )}
                                </Headline>
                                <View style={styles.buttonHolder}>
                                    <Button
                                        onPress={async () =>
                                            await onChangePin()
                                        }
                                        variant="outline"
                                        size="tiny"
                                        type="warning"
                                        style={styles.smallButton}
                                    >
                                        {t(
                                            "backoffice.integration.fiskaly.change_pin_for_tss",
                                            "Forgotten PIN?"
                                        )}
                                    </Button>
                                </View>
                            </>
                        ) : null}
                    </>
                ) : null}
            </View>
        </>
    );
}

const styleFunc: StyleFunction = theme => ({
    buttonHolder: {
        ...theme.styles.row,
        gap: theme.spacingScale,
    },

    smallHeader: {
        color: theme.colors.textDark,
        fontSize: 12,
        margin: 0,
        lineHeight: 12,
    },

    smallButton: {
        paddingHorizontal: theme.spacingScale * 1.5,
    },

    gap: {
        gap: theme.spacingScale,
    },
});
